import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface AdminRouteProps {
  children: React.ReactNode;
}

export const AdminRoute = ({ children }: AdminRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isSuperAdminRemote, setIsSuperAdminRemote] = useState<boolean>(false);
  const [hasParoquia, setHasParoquia] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAdminAndParoquia = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Check if super_admin with active remote session
        const remoteParoquiaId = sessionStorage.getItem("remote_viewing_paroquia_id");
        if (remoteParoquiaId) {
          const { data: isSuperAdmin } = await supabase.rpc('has_role', {
            _user_id: user.id,
            _role: 'super_admin'
          });

          if (isSuperAdmin) {
            // Verify active session exists
            const { data: session } = await supabase
              .from("remote_access_sessions")
              .select("id")
              .eq("paroquia_id", remoteParoquiaId)
              .eq("requested_by", user.id)
              .eq("status", "approved")
              .gt("expires_at", new Date().toISOString())
              .limit(1)
              .maybeSingle();

            if (session) {
              setIsSuperAdminRemote(true);
              setIsAdmin(true);
              setHasParoquia(true);
              setLoading(false);
              return;
            } else {
              // Session expired/invalid, clear it
              sessionStorage.removeItem("remote_viewing_paroquia_id");
            }
          }
        }

        // Check admin role
        const { data: isAdminData, error: roleError } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin'
        });

        if (roleError) {
          console.error('Error checking admin role:', roleError);
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        setIsAdmin(isAdminData === true);

        // If admin, check if has paroquia linked
        if (isAdminData === true) {
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("paroquia_id")
            .eq("id", user.id)
            .single();

          if (profileError) {
            console.error('Error checking profile:', profileError);
            setHasParoquia(false);
          } else {
            setHasParoquia(!!profile?.paroquia_id);
          }
        }
      } catch (err) {
        console.error('Error:', err);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      if (!user) {
        navigate("/auth");
      } else {
        checkAdminAndParoquia();
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!loading && isAdmin === false && !isSuperAdminRemote) {
      navigate("/home");
      return;
    }

    // If admin without paroquia, redirect to onboarding (unless already there or remote)
    if (!loading && isAdmin === true && !isSuperAdminRemote && hasParoquia === false && location.pathname !== "/admin/onboarding") {
      navigate("/admin/onboarding");
    }
  }, [loading, isAdmin, isSuperAdminRemote, hasParoquia, navigate, location.pathname]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  if (!user || (!isAdmin && !isSuperAdminRemote)) {
    return null;
  }

  // Allow access to onboarding even without paroquia (skip for remote)
  if (!isSuperAdminRemote && !hasParoquia && location.pathname !== "/admin/onboarding") {
    return null;
  }

  return <>{children}</>;
};
